'use client';
import React, { useCallback, useEffect, useState } from 'react';

import { useScreenSizeLegacy } from '@/hooks/useScreenSizeLegacy';

import { cn } from '@/lib/utils';

import { useHasUserInteracted } from '@/components/organisms/VideoAccordion/WithHasUserInteracted';

import AccordionItem, {
  AccordionItemProps,
} from '@/components/molecules/accordion/AccordionItem';
import {
  AccordionProvider,
  useAccordion,
} from '@/components/molecules/accordion/AccordionProvider';

export interface AccordionListProps {
  accordionList: {
    header: React.ReactNode;
    children: React.ReactNode;
  }[];
}
export const DEFAULT_ACCORDION_TIMER_DURATION_SEC = 7;

export type AccordionListItemProps = {
  isMultiMode?: boolean;
  internal?: {
    timer?: {
      durationSec?: number; // in seconds, defaults to 7
    };
    ContainerProps?: React.HTMLAttributes<HTMLDivElement>;
    accordionItem?: Partial<AccordionItemProps>;
  };
} & AccordionListProps;

export function AccordionListComponent({
  accordionList,
  internal,
}: AccordionListItemProps) {
  const { accordionItem, ContainerProps } = internal || {};
  const { openItemId, setOpenItemId } = useAccordion();
  const [timerKey, setTimerKey] = useState(0);
  const { isDesktop } = useScreenSizeLegacy();
  const { hasUserInteracted, setHasUserInteracted } = useHasUserInteracted();

  useEffect(() => {
    if (!internal?.timer?.durationSec) return;
    if (hasUserInteracted) return;
    if (!isDesktop) return;

    const currentIndex = Number(openItemId ?? 0);
    const nextIndex = (currentIndex + 1) % accordionList.length;

    const duration =
      internal.timer.durationSec ?? DEFAULT_ACCORDION_TIMER_DURATION_SEC;

    const timer = setTimeout(() => {
      setOpenItemId(String(nextIndex));
    }, duration * 1000);

    setTimerKey((prev) => prev + 1);

    return () => clearTimeout(timer);
  }, [
    openItemId,
    accordionList.length,
    setOpenItemId,
    hasUserInteracted,
    isDesktop,
    internal?.timer?.durationSec,
  ]);

  const handleUserClick = useCallback(
    (id: string) => {
      setHasUserInteracted(true);
      setOpenItemId(id);
    },
    [setOpenItemId, setHasUserInteracted]
  );

  return (
    <div
      {...ContainerProps}
      data-tracker-component='accordion'
      className={cn(
        'flex w-full flex-col text-left',
        ContainerProps?.className
      )}
    >
      {accordionList?.map((item, index) => {
        const id = String(index);
        const isActive = id === openItemId;

        return (
          <div key={id} className='relative'>
            <div onClick={() => handleUserClick(id)}>
              <AccordionItem {...accordionItem} id={id} header={item.header}>
                {item.children}
              </AccordionItem>

              {isActive &&
                !hasUserInteracted &&
                !!internal?.timer?.durationSec && (
                  <div
                    key={`timer-${timerKey}`}
                    className={cn(
                      'bg-gradient-secondary-4 absolute bottom-0 left-0 hidden h-[3px] origin-left md:flex',
                      'animate-scale-x-linear'
                    )}
                    style={{
                      width: '100%',
                      ['--duration' as any]: `${internal?.timer?.durationSec}s`,
                    }}
                  />
                )}
            </div>
          </div>
        );
      })}
      <div className='border-b border-solid border-gray-200' />
    </div>
  );
}

export default function AccordionList(props: AccordionListItemProps) {
  const { isMultiMode = false, ...rest } = props;
  return (
    <AccordionProvider isMultiMode={isMultiMode}>
      <AccordionListComponent {...rest} />
    </AccordionProvider>
  );
}
